import axios from 'axios';
import { CONSTANTS } from '../../service/constants';
import { getCachedStaticData } from '../../service/staticData';

const normalizeCountry = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

const DOMAIN_COUNTRY_NAMES = {
  no: 'Norway',
  dk: 'Denmark',
  se: 'Sweden',
  fi: 'Finland',
};

const getDomainCountryName = () => {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname.toLowerCase();
  if (host.endsWith('.no')) return DOMAIN_COUNTRY_NAMES.no;
  if (host.endsWith('.dk')) return DOMAIN_COUNTRY_NAMES.dk;
  if (host.endsWith('.se')) return DOMAIN_COUNTRY_NAMES.se;
  if (host.endsWith('.fi')) return DOMAIN_COUNTRY_NAMES.fi;
  return null;
};

const getDomainCountryId = () => {
  const name = getDomainCountryName();
  if (!name) return null;
  const cached = getCachedStaticData();
  const countries = Array.isArray(cached?.countries) ? cached.countries : [];
  const normalized = normalizeCountry(name);
  const match = countries.find((country) => normalizeCountry(country?.name) === normalized);
  return match?.id ?? null;
};

const COUNTRY_ALIASES = {
  no: ['no', 'norway', 'norge', 'noreg'],
  dk: ['dk', 'denmark', 'danmark'],
  se: ['se', 'sweden', 'sverige'],
  fi: ['fi', 'finland', 'suomi'],
  us: ['us', 'usa', 'united states', 'america'],
  gb: ['gb', 'uk', 'united kingdom', 'britain', 'england'],
  ca: ['ca', 'canada'],
  au: ['au', 'australia'],
  nz: ['nz', 'new zealand'],
  ie: ['ie', 'ireland'],
};

const getPreferredCountries = () => {
  if (typeof window === 'undefined') return [];
  const host = window.location.hostname.toLowerCase();
  if (host.endsWith('.no')) return ['no'];
  if (host.endsWith('.dk')) return ['dk'];
  if (host.endsWith('.se')) return ['se'];
  if (host.endsWith('.fi')) return ['fi'];
  if (host.endsWith('.com')) return ['us', 'gb', 'ca', 'au', 'nz', 'ie'];
  return [];
};

const isPreferredCountry = (item, preferred) => {
  if (!item || item.type !== 'grave' || preferred.length === 0) return false;
  const code = normalizeCountry(item.countryCode);
  const name = normalizeCountry(item.countryName);
  return preferred.some((pref) => {
    const aliases = COUNTRY_ALIASES[pref] || [pref];
    return aliases.some((alias) => alias === code || alias === name);
  });
};

const prioritizeLocalResults = (items, query) => {
  const term = query?.trim() || '';
  if (term.length > 2) return items;
  const preferred = getPreferredCountries();
  if (preferred.length === 0) return items;
  const normalized = preferred.map((c) => normalizeCountry(c));
  return items
    .map((item, idx) => ({
      item,
      idx,
      local: isPreferredCountry(item, normalized),
    }))
    .sort((a, b) => {
      if (a.local !== b.local) return a.local ? -1 : 1;
      return a.idx - b.idx;
    })
    .map(({ item }) => item);
};

export const balanceUnifiedResults = (items, limit = 5) => {
  const safeLimit = Math.max(1, Number(limit) || 5);
  const members = items.filter((item) => item.type === 'member');
  const graves = items.filter((item) => item.type === 'grave');
  const others = items.filter((item) => !['member', 'grave'].includes(item.type));

  if (members.length === 0 || graves.length === 0) return items.slice(0, safeLimit);

  const memberSlots = Math.min(2, members.length, Math.max(1, safeLimit - 1));
  const selectedMembers = members.slice(0, memberSlots);
  const selectedGraves = graves.slice(0, safeLimit - selectedMembers.length);
  const selectedIds = new Set([...selectedMembers, ...selectedGraves]);
  const remaining = [...members, ...graves, ...others].filter((item) => !selectedIds.has(item));

  return [...selectedMembers, ...selectedGraves, ...remaining].slice(0, safeLimit);
};

/**
 * Unified search fetcher for graves and members.
 * @param {string} q
 * @param {number} [limit=5]
 * @returns {Promise<Array<{id:string|number,type:string,title:string,subtitle?:string,href:string}>>}
 */
export const unifiedSearchFetcher = async (q, limit = 5) => {
  const term = q?.trim();
  if (!term) return [];
  const searchLimit = term.length <= 2 ? Math.max(limit, 20) : limit;
  const countryId = term.length <= 2 ? getDomainCountryId() : null;
  try {
    const params = {
      search: {
        query: term,
        tab: 'all',
        filter: countryId ? { country_id: countryId } : undefined,
      },
      per_page: searchLimit,
      page: 1,
      lite: 1,
    };
    const { data } = await axios.get(`${CONSTANTS.baseUrl}/api/graves`, { params });
    const graves = (data?.display_graves || data?.graves || []).filter(
      // Filter out non-entity payloads (e.g., ads) by requiring slug/name
      (g) => g && (g.slug || g.id) && (g.name || g.title) && !g.ad_id && !g.placement,
    );
    const users = Array.isArray(data?.users)
      ? data.users
      : Array.isArray(data?.data)
        ? data.data
        : [];
    const items = [
      ...graves.map((g) => ({
        id: g.id || g.key,
        type: 'grave',
        title: g.name,
        subtitle: g.cemetery || g.graveyard?.name,
        countryCode: g.country?.code || g.country?.iso2 || g.country_code || g.country?.iso,
        countryName: g.country?.name || g.country_name,
        href: g.id ? `/graves/${g.slug || g.id}` : '#',
      })),
      ...users.map((m) => ({
        id: m.id,
        type: 'member',
        title: m.full_name || m.name,
        subtitle: m.city,
        href: m.slug ? `/members/${m.slug}` : `/members/${m.id}`,
      })),
    ]
      // Ensure items are navigable and reasonably formed
      .filter((it) => it && it.href && it.href !== '#');
    return balanceUnifiedResults(prioritizeLocalResults(items, term), limit);
  } catch {
    try {
      const params = { input: term, page: 1, per_page: searchLimit, lite: 1 };
      if (countryId) params.country_id = countryId;
      const { data } = await axios.get(`${CONSTANTS.baseUrl}/api/search_graves`, { params });
      const graves = Array.isArray(data?.graves) ? data.graves : [];
      return graves
        .filter((grave) => grave && (grave.slug || grave.id) && grave.name)
        .map((grave) => ({
          id: grave.id || grave.key,
          type: 'grave',
          title: grave.name,
          subtitle: grave.cemetery || grave.graveyard?.name,
          href: `/graves/${grave.slug || grave.id}`,
        }))
        .slice(0, limit);
    } catch {
      return [];
    }
  }
};
